/**
 * @file
 * Provides information about the current interaction methods
 */
const DEBOUNCE = 200;
const FORM_INPUT_ELEMENTS = ["BUTTON", "INPUT", "SELECT", "TEXTAREA"]; // Mapping of events to interaciton methods

const METHOD_MAP = {
  keydown: "keyboard",
  keyup: "keyboard",
  mousedown: "mouse",
  mousemove: "mouse",
  touchend: "touch",
  touchstart: "touch",
  wheel: "mouse",
}; // List of modifier keys commonly used with the mouse and
// can be safely ignored to prevent false keyboard detection

const IGNORE_KEYS = ["Shift", "Control", "Alt", "Meta"]; // Check support for passive event listeners

function supportsPassive() {
  try {
    window.addEventListener(
      "input",
      () => null,
      Object.defineProperty({}, "passive", {
        get: () => true,
      })
    );
    return true;
  } catch (e) {
    return false;
  }
}
/**
 * @classdesc Singleton
 */

class InteractionMethodObserver {
  static getInstance() {
    if (!InteractionMethodObserver.instance) {
      InteractionMethodObserver.instance = new InteractionMethodObserver();
    }

    return InteractionMethodObserver.instance;
  }

  observers = new Set();
  input = "initial";
  control = "initial";
  ignoreKeys = IGNORE_KEYS;
  lastTouchTimestamp = Date.now();
  lastScrollTimestamp = null;

  constructor() {
    const options = supportsPassive()
      ? {
          passive: true,
        }
      : false;
    window.addEventListener("mousedown", this.handleInput);
    window.addEventListener("mousemove", this.handleControl);

    if ("ontouchstart" in window) {
      window.addEventListener("touchstart", this.handleInput, options);
      window.addEventListener("touchend", this.handleInput);
    }

    window.addEventListener("wheel", this.handleControl, options);
    window.addEventListener("keydown", this.handleInput);
    window.addEventListener("keyup", this.handleInput);
    window.addEventListener("scroll", this.handleScroll);
  }

  get state() {
    return {
      input: this.input,
      control: this.control,
    };
  }

  subscribe(handler) {
    this.observers.add(handler);
  }

  unsubscribe(handler) {
    this.observers.delete(handler);
  }

  publish() {
    this.observers.forEach((handler) => {
      handler(this.state);
    });
  }

  get isScrolling() {
    return (
      this.lastScrollTimestamp &&
      Date.now() < this.lastScrollTimestamp + DEBOUNCE
    );
  } // Mobile devices fire both mouse and touch events.
  // Prevent touch detection from being overridden by event execution order.

  validateTouch(method) {
    const timestamp = Date.now();
    const touchIsValid =
      method === "mouse" &&
      this.input === "touch" &&
      timestamp - this.lastTouchTimestamp < DEBOUNCE;
    this.lastTouchTimestamp = timestamp;
    return touchIsValid;
  }

  handleScroll = () => {
    this.lastScrollTimestamp = Date.now();
  };
  handleInput = (event) => {
    const eventKey = "key" in event ? event.key : undefined;
    const method = METHOD_MAP[event.type];
    const isIgnored = eventKey && this.ignoreKeys.indexOf(eventKey) > -1;
    const shouldUpdate =
      (method === "keyboard" && !isIgnored) ||
      (method === "mouse" && !this.validateTouch(method)) ||
      method === "touch";

    if (shouldUpdate && this.input !== method) {
      this.input = method;
    }

    if (shouldUpdate && this.control !== method) {
      // Preserve non-mouse control when clicking/tapping on form inputs
      const { activeElement } = document;
      const notFormInput =
        activeElement &&
        (!FORM_INPUT_ELEMENTS.includes(activeElement.tagName) ||
          (activeElement.tagName === "BUTTON" &&
            !activeElement.closest("form")));

      if (notFormInput) {
        this.control = method;
      }
    }

    this.publish();
  };
  handleControl = (event) => {
    const method = METHOD_MAP[event.type];
    const shouldUpdate =
      (!this.isScrolling && !this.validateTouch(method)) ||
      (this.isScrolling && event.type === "wheel");

    if (shouldUpdate && this.control !== method) {
      this.control = method;
    }

    this.publish();
  };
}
const instance = InteractionMethodObserver.getInstance();
function getCurrentInteractionMethod(type) {
  return instance.state[type];
}

export default instance;
export { InteractionMethodObserver, getCurrentInteractionMethod };
